import ApplicationController from './application_controller';
import initializeDataTable from '../lib/datatables/initialize';
import log from '../lib/logger';
import { loadJQueryDataTables } from '../lib/datatables/load_jquery_datatables';

export default class extends ApplicationController {
  static targets = [
    // Billing
    'agingReportDataTable',
    'authorizationsDataTable',
    'insuranceCptDataTable',
    'insuranceSummaryDataTable',
    'patientBalanceDataTable',
    'patientReversalsDataTable',
    'patientReversalsPatientDataTable',
    'cptCodeSummaryDataTable',
    'locationSummaryDataTable',
    'billingTriageRadarChart',
    'cptRevenueDataTable',
    'therapistRevenueDataTable',
    'locationRevenueDataTable',
    // Clinical documentation
    'visitNoteBreakdownDataTable',
    'evaluationBreakdownDataTable',
    'dischargesDataTable',
    'eventsMissingDocumentationDataTable',
    'eventsMissingAttendanceDataTable',
    'unsignedClinicalDocumentationDataTable',
    // Attendance
    'clinicCanceledDataTable',
    'clientCanceledDataTable',
    'noShowDataTable',
    'missedSessionLogsDataTable',
    'presentDataTable',
    'sessionsListDataTable',
    // Key metrics
    'monthlyStatsDataTable',
    'therapistStatsDataTable',
    // Locations
    'locationsDataTable',
  ];

  async authorizationsDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#authorizations-table')) {
      window.$('#authorizations-table').DataTable().destroy();
    }
    window.$('#authorizations-table').DataTable({
      order: [[7, 'asc']],
    });
  }

  billingTriageRadarChartTargetConnected() {
    this.initBillingRadarChart();
  }

  initBillingRadarChart() {
    window.pageJSLoading = true;
    import('../pages/billing_health')
      .then((module) => {
        const billingHealth = module.default;
        if (billingHealth !== undefined) {
          billingHealth();
        }
        window.pageJSLoading = false;
      })
      .catch((error) => {
        log.error('Error loading billing health module:', error);
        window.pageJSLoading = false;
      });
  }

  async agingReportDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#aging-table')) {
      window.$('#aging-table').DataTable().destroy();
    }
    window.$('#aging-table').DataTable({
      order: [[1, 'desc']],
    });
  }

  async visitNoteBreakdownDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#visit-note-table')) {
      window.$('#visit-note-table').DataTable().destroy();
    }
    window.$('#visit-note-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  async evaluationBreakdownDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#evaluation-table')) {
      window.$('#evaluation-table').DataTable().destroy();
    }
    window.$('#evaluation-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  async dischargesDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#discharge-table')) {
      window.$('#discharge-table').DataTable().destroy();
    }
    window.$('#discharge-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  async eventsMissingDocumentationDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#events-missing-documentation-table')) {
      window.$('#events-missing-documentation-table').DataTable().destroy();
    }
    window.$('#events-missing-documentation-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  async patientBalanceDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#patient-balances-table')) {
      window.$('#patient-balances-table').DataTable().destroy();
    }
    window.$('#patient-balances-table').DataTable({
      order: [[1, 'desc']],
    });
  }

  async patientReversalsDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#payment-reversals-by-payer-table')) {
      window.$('#payment-reversals-by-payer-table').DataTable().destroy();
    }

    const columnCount = window.$('#payment-reversals-by-payer-table thead th').length;
    window.$('#payment-reversals-by-payer-table').DataTable({
      order: [[columnCount - 1, 'desc']],
    });
  }

  async patientReversalsPatientDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#payment-reversals-by-patient-table')) {
      window.$('#payment-reversals-by-patient-table').DataTable().destroy();
    }

    const columnCount = window.$('#payment-reversals-by-patient-table thead th').length;
    window.$('#payment-reversals-by-patient-table').DataTable({
      order: [[columnCount - 2, 'desc']],
    });
  }

  async insuranceCptDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#insurance-cpt-payments-table')) {
      window.$('#insurance-cpt-payments-table').DataTable().destroy();
    }
    window.$('#insurance-cpt-payments-table').DataTable({
      order: [[4, 'desc']],
    });
  }

  async insuranceSummaryDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#insurance-summary-table')) {
      window.$('#insurance-summary-table').DataTable().destroy();
    }
    window.$('#insurance-summary-table').DataTable({
      order: [[2, 'desc']],
    });
  }

  async cptCodeSummaryDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#cpt-summary-table')) {
      window.$('#cpt-summary-table').DataTable().destroy();
    }
    window.$('#cpt-summary-table').DataTable({
      order: [[3, 'desc']],
    });
  }

  async locationSummaryDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#location-summary-table')) {
      window.$('#location-summary-table').DataTable().destroy();
    }
    window.$('#location-summary-table').DataTable({
      order: [[2, 'desc']],
    });
  }

  async therapistRevenueDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#therapist-revenue-table')) {
      window.$('#therapist-revenue-table').DataTable().destroy();
    }
    window.$('#therapist-revenue-table').DataTable({
      order: [[5, 'desc']],
    });
  }

  async locationRevenueDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#location-revenue-table')) {
      window.$('#location-revenue-table').DataTable().destroy();
    }
    window.$('#location-revenue-table').DataTable({
      order: [[5, 'desc']],
    });
  }

  async cptRevenueDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#cpt-revenue-table')) {
      window.$('#cpt-revenue-table').DataTable().destroy();
    }
    window.$('#cpt-revenue-table').DataTable({
      order: [[6, 'desc']],
    });
  }

  async eventsMissingAttendanceDataTableTargetConnected(target) {
    if (!(await this.dataTablesReady(target))) return;
    if (window.$.fn.dataTable.isDataTable('#events-missing-attendance-table')) {
      window.$('#events-missing-attendance-table').DataTable().destroy();
    }
    window.$('#events-missing-attendance-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  unsignedClinicalDocumentationDataTableTargetConnected() {
    initializeDataTable();
  }

  clinicCanceledDataTableTargetConnected(element) {
    this.attendancePageDataTableInit(element);
  }

  clientCanceledDataTableTargetConnected(element) {
    this.attendancePageDataTableInit(element);
  }

  noShowDataTableTargetConnected(element) {
    this.attendancePageDataTableInit(element);
  }

  async missedSessionLogsDataTableTargetConnected(element) {
    if (!(await this.dataTablesReady(element))) return;
    const sessionLogs = window.$('.missed-session-logs-table', element)[0];
    if (sessionLogs && window.$.fn.dataTable.isDataTable(sessionLogs)) {
      window.$(sessionLogs).DataTable().destroy();
    }
    window.$(sessionLogs).DataTable({
      order: [[4, 'desc']],
    });
  }

  async monthlyStatsDataTableTargetConnected(element) {
    if (!(await this.dataTablesReady(element))) return;
    const keyMetricsMonthly = window.$('#key-metrics-monthly', element)[0];
    if (keyMetricsMonthly && window.$.fn.dataTable.isDataTable(keyMetricsMonthly)) {
      window.$(keyMetricsMonthly).DataTable().destroy();
    }
    window.$(keyMetricsMonthly).DataTable({
      order: [[0, 'desc']],
    });
  }

  async therapistStatsDataTableTargetConnected(element) {
    if (!(await this.dataTablesReady(element))) return;
    const keyMetricsMonthly = window.$('#key-metrics-therapist', element)[0];
    if (keyMetricsMonthly && window.$.fn.dataTable.isDataTable(keyMetricsMonthly)) {
      window.$(keyMetricsMonthly).DataTable().destroy();
    }
    window.$(keyMetricsMonthly).DataTable({
      order: [[8, 'desc']],
    });

    // Selectize the late-arriving dropdown (the pack's turbo:load init has already
    // run). Navigation is handled by the select-navigate controller on the element.
    $('#dateDropdown').selectize();
  }

  // One target serves every Locations tab: each table carries its own ordering attributes.
  async locationsDataTableTargetConnected(element) {
    if (!(await this.dataTablesReady(element))) return;
    window.$('table.locations-data-table', element).each((_index, table) => {
      if (window.$.fn.dataTable.isDataTable(table)) {
        window.$(table).DataTable().destroy();
      }
      const orderColumn = parseInt(table.dataset.orderColumn || '1', 10);
      window.$(table).DataTable({
        order: [[orderColumn, table.dataset.orderDirection || 'desc']],
        pageLength: 25,
      });
    });
  }

  async presentDataTableTargetConnected(element) {
    if (!(await this.dataTablesReady(element))) return;
    const patientsAttendanceRates = window.$('#patients-attendance-rates', element)[0];
    if (patientsAttendanceRates && window.$.fn.dataTable.isDataTable(patientsAttendanceRates)) {
      window.$(patientsAttendanceRates).DataTable().destroy();
    }
    window.$(patientsAttendanceRates).DataTable({
      order: [[5, 'desc']],
    });
    const therapistAttendanceRates = window.$('#therapists-attendance-rates', element)[0];
    if (therapistAttendanceRates && window.$.fn.dataTable.isDataTable(therapistAttendanceRates)) {
      window.$(therapistAttendanceRates).DataTable().destroy();
    }
    window.$(therapistAttendanceRates).DataTable({
      order: [[5, 'desc']],
    });
  }

  async sessionsListDataTableTargetConnected(element) {
    if (!(await this.dataTablesReady(element))) return;
    const sessionsListTable = window.$('#sessions-list-table', element)[0];
    if (sessionsListTable && window.$.fn.dataTable.isDataTable(sessionsListTable)) {
      window.$(sessionsListTable).DataTable().destroy();
    }
    window.$(sessionsListTable).DataTable({
      order: [[0, 'desc']],
      pageLength: 50,
    });
  }

  async attendancePageDataTableInit(element) {
    if (!(await this.dataTablesReady(element))) return;
    const patients = window.$('.patients-data-table', element)[0];
    if (patients && window.$.fn.dataTable.isDataTable(patients)) {
      window.$(patients).DataTable().destroy();
    }
    window.$(patients).DataTable({
      order: [[2, 'desc']],
    });

    const therapists = window.$('.therapists-data-table', element)[0];
    if (therapists && window.$.fn.dataTable.isDataTable(therapists)) {
      window.$(therapists).DataTable().destroy();
    }
    window.$(therapists).DataTable({
      order: [[2, 'desc']],
    });

    const hours = window.$('.hours-data-table', element)[0];
    if (hours && window.$.fn.dataTable.isDataTable(hours)) {
      window.$(hours).DataTable().destroy();
    }
    window.$(hours).DataTable({
      order: [[1, 'desc']],
    });

    const days = window.$('.days-data-table', element)[0];
    if (days && window.$.fn.dataTable.isDataTable(days)) {
      window.$(days).DataTable().destroy();
    }
    window.$(days).DataTable({
      order: [[1, 'desc']],
    });
  }

  async dataTablesReady(target) {
    await loadJQueryDataTables();
    return target.isConnected;
  }
}
